'use client';

import clsx from 'clsx';
import { type CSSProperties } from 'react';
import Accordion, {
  AccordionButton,
  AccordionPanel,
  useAccordion,
  type AccordionItemType,
} from '@publishing/components/Accordion';
import Icon from '@publishing/components/ui/Icon';
import { ArrowChevron } from '@publishing/components/ui/SvgSprite';
import { resolveColor } from '@publishing/helpers/format';
import s from './AccordionItem.module.scss';
import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';

export type { AccordionItemType };

export interface AccordionListProps {
  items: AccordionItemType[];
  className?: string;
  index?: number;
  bgColor?: string;
  titleColor?: string;
  textColor?: string;
}

export default function AccordionList({ items, className, index = 0, bgColor, titleColor, textColor }: AccordionListProps) {
  const customStyle = {
    '--accordion-bg': bgColor ? resolveColor(bgColor) : undefined,
    '--accordion-title-color': titleColor ? resolveColor(titleColor) : undefined,
    '--accordion-text-color': textColor ? resolveColor(textColor) : undefined,
  } as CSSProperties;

  return (
    <Accordion index={index}>
      <ul className={className} style={customStyle}>
        {items.map((item, idx) => (
          <AccordionListItem
            key={`${String(item.question)}-${idx}`}
            item={item}
            idx={idx}
          />
        ))}
      </ul>
    </Accordion>
  );
}

interface AccordionListItemProps {
  item: AccordionItemType;
  idx: number;
}

function AccordionListItem({
  item,
  idx,
}: AccordionListItemProps) {
  const { question, answer, note } = item;
  const { openIndex } = useAccordion();
  const itemCls = clsx(s.accordion_item, openIndex === idx && s.open);

  return (
    <li className={itemCls}>
      <AccordionButton
        index={idx}
        className={s.accordion_button}
        // 시트 row 47 `click_toggle` — 클릭 시 내용 숨김을 ON/OFF 하는 스위치.
        // question 은 ReactNode 라 문자열일 때만 라벨로 쓴다.
        {...(typeof question === 'string'
          ? gtmClick(GTM_EVENT.TOGGLE, question)
          : {})}
      >
        <strong className={s.accordion_title}>{question}</strong>
        <span className={s.accordion_icon}>
          <Icon name={ArrowChevron} direction="up" size={24} />
        </span>
      </AccordionButton>
      <AccordionPanel
        index={idx}
        className={s.accordion_answer}
      >
        <div className={s.accordion_answer_inner}>
          <div className={s.accordion_answer_content}>
            <p className={s.accordion_text}>{answer}</p>
            {note && <span className={s.accordion_note}>{note}</span>}
          </div>
        </div>
      </AccordionPanel>
    </li>
  );
}
